"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LiquityStore = void 0;
const assert_1 = __importDefault(require("assert"));
const strictEquals = (a, b) => a === b;
const eq = (a, b) => a.eq(b);
const equals = (a, b) => a.equals(b);
const wrap = (f) => (...args) => f(...args);
const difference = (a, b) => Object.fromEntries(Object.entries(a).filter(([key, value]) => value !== b[key]));
/**
 * Abstract base class of Liquity data store implementations.
 *
 * @remarks
 * The type parameter `T` may be used to type extra state added to {@link LiquityStoreState} by the
 * subclass.
 *
 * Implemented by {@link @liquity/lib-ethers#BlockPolledLiquityStore}.
 *
 * @public
 */
class LiquityStore {
    constructor() {
        /** Turn console logging on/off. */
        this.logging = false;
        /** @internal */
        this._loaded = false;
        this._listeners = new Set();
    }
    /**
     * The current store state.
     *
     * @remarks
     * Should not be accessed before the store is loaded. Assign a function to
     * {@link LiquityStore.onLoaded | onLoaded} to get a callback when this happens.
     *
     * See {@link LiquityStoreState} for the list of properties returned.
     */
    get state() {
        return Object.assign({}, this._baseState, this._derivedState, this._extraState);
    }
    /**
     * Start monitoring the blockchain for Liquity state changes.
     *
     * @remarks
     * The {@link LiquityStore.onLoaded | onLoaded} callback will be called after the state is fetched
     * for the first time.
     *
     * Use the {@link LiquityStore.subscribe | subscribe()} function to register listeners.
     *
     * @returns Function to stop the monitoring.
     */
    start() {
        const doStop = this._doStart();
        return () => {
            doStop();
            this._cancelUpdateIfScheduled();
        };
    }
    _cancelUpdateIfScheduled() {
        if (this._updateTimeoutId !== undefined) {
            clearTimeout(this._updateTimeoutId);
        }
    }
    _scheduleUpdate() {
        this._cancelUpdateIfScheduled();
        this._updateTimeoutId = setTimeout(() => {
            this._updateTimeoutId = undefined;
            this._update();
        }, 30000);
    }
    _logUpdate(name, next, show) {
        if (this.logging) {
            console.log(`${name} updated to ${show ? show(next) : next}`);
        }
        return next;
    }
    _updateIfChanged(equals, name, prev, next, show) {
        return next !== undefined && !equals(prev, next) ? this._logUpdate(name, next, show) : prev;
    }
    _silentlyUpdateIfChanged(equals, prev, next) {
        return next !== undefined && !equals(prev, next) ? next : prev;
    }
    _updateFees(name, prev, next) {
        if (next && !next.equals(prev)) {
            // Filter out fee update spam that happens on every new block by only logging when string
            // representation changes.
            if (`${next}` !== `${prev}`) {
                this._logUpdate(name, next);
            }
            return next;
        }
        else {
            return prev;
        }
    }
    _reduce(baseState, baseStateUpdate) {
        const base = {
            slsdBalance: this._updateIfChanged(eq, "slsdBalance", baseState.slsdBalance, baseStateUpdate.slsdBalance),
            psyBalance: this._updateIfChanged(eq, "psyBalance", baseState.psyBalance, baseStateUpdate.psyBalance),
        };
        const collaterals = Object.entries(baseState.collateralStates).reduce((acc, [collateral, collateralState]) => {
            if (!baseStateUpdate.collateralStates) {
                return acc;
            }
            const collateralStateUpdate = baseStateUpdate.collateralStates[collateral];
            acc[collateral] = {
                // TODO: collateral毎に実行するように変更
                numberOfTroves: this._updateIfChanged(strictEquals, "numberOfTroves", collateralState.numberOfTroves, collateralStateUpdate.numberOfTroves),
                balance: this._updateIfChanged(eq, "balance", collateralState.balance, collateralStateUpdate.balance),
                allowance: this._updateIfChanged(eq, "allowance", collateralState.allowance, collateralStateUpdate.allowance),
                collateralSurplusBalance: this._updateIfChanged(eq, "collateralSurplusBalance", collateralState.collateralSurplusBalance, collateralStateUpdate.collateralSurplusBalance),
                price: this._updateIfChanged(eq, "price", collateralState.price, collateralStateUpdate.price),
                slsdInStabilityPool: this._updateIfChanged(eq, "slsdInStabilityPool", collateralState.slsdInStabilityPool, collateralStateUpdate.slsdInStabilityPool),
                total: this._updateIfChanged(equals, "total", collateralState.total, collateralStateUpdate.total),
                totalRedistributed: this._updateIfChanged(equals, "totalRedistributed", collateralState.totalRedistributed, collateralStateUpdate.totalRedistributed),
                troveBeforeRedistribution: this._updateIfChanged(equals, "troveBeforeRedistribution", collateralState.troveBeforeRedistribution, collateralStateUpdate.troveBeforeRedistribution),
                stabilityDeposit: this._updateIfChanged(equals, "stabilityDeposit", collateralState.stabilityDeposit, collateralStateUpdate.stabilityDeposit),
                remainingStabilityPoolPSYReward: this._silentlyUpdateIfChanged(eq, collateralState.remainingStabilityPoolPSYReward, collateralStateUpdate.remainingStabilityPoolPSYReward),
                _feesInNormalMode: this._silentlyUpdateIfChanged(equals, collateralState._feesInNormalMode, collateralStateUpdate._feesInNormalMode),
                _riskiestTroveBeforeRedistribution: this._silentlyUpdateIfChanged(equals, collateralState._riskiestTroveBeforeRedistribution, collateralStateUpdate._riskiestTroveBeforeRedistribution),
                isMinted: this._updateIfChanged(strictEquals, "isMinted", collateralState.isMinted, collateralStateUpdate.isMinted),
            };
            return acc;
        }, {});
        return Object.assign({}, base, { collateralStates: collaterals });
    }
    _derive({ collateralStates }) {
        const collaterals = Object.entries(collateralStates).reduce((acc, [collateral, collateralState]) => {
            const { troveBeforeRedistribution, totalRedistributed, _feesInNormalMode, total, price, _riskiestTroveBeforeRedistribution } = collateralState;
            const fees = _feesInNormalMode._setRecoveryMode(total.collateralRatioIsBelowCritical(price));
            acc[collateral] = {
                trove: troveBeforeRedistribution.applyRedistribution(totalRedistributed),
                fees,
                borrowingRate: fees.borrowingRate(),
                redemptionRate: fees.redemptionRate(),
                haveUndercollateralizedTroves: _riskiestTroveBeforeRedistribution
                    .applyRedistribution(totalRedistributed)
                    .collateralRatioIsBelowMinimum(price)
            };
            return acc;
        }, {});
        return { collateralDerivedStates: collaterals };
    }
    _reduceDerived(derivedState, derivedStateUpdate) {
        const collaterals = Object.entries(derivedState.collateralDerivedStates).reduce((acc, [collateral, collateralDerivedState]) => {
            const collateralDerivedStateUpdate = derivedStateUpdate.collateralDerivedStates[collateral];
            acc[collateral] = {
                fees: this._updateFees("fees", collateralDerivedState.fees, collateralDerivedStateUpdate.fees),
                trove: this._updateIfChanged(equals, "trove", collateralDerivedState.trove, collateralDerivedStateUpdate.trove),
                borrowingRate: this._silentlyUpdateIfChanged(eq, collateralDerivedState.borrowingRate, collateralDerivedStateUpdate.borrowingRate),
                redemptionRate: this._silentlyUpdateIfChanged(eq, collateralDerivedState.redemptionRate, collateralDerivedStateUpdate.redemptionRate),
                haveUndercollateralizedTroves: this._updateIfChanged(strictEquals, "haveUndercollateralizedTroves", collateralDerivedState.haveUndercollateralizedTroves, collateralDerivedStateUpdate.haveUndercollateralizedTroves)
            };
            return acc;
        }, {});
        return { collateralDerivedStates: collaterals };
    }
    _notify(params) {
        // Iterate on a copy of `_listeners`, to avoid notifying any new listeners subscribed by
        // existing listeners, as that could result in infinite loops.
        //
        // Before calling a listener from our copy of `_listeners`, check if it has been removed from
        // the original set. This way we avoid calling listeners that have already been unsubscribed
        // by an earlier listener callback.
        [...this._listeners].forEach(listener => {
            if (this._listeners.has(listener)) {
                listener(params);
            }
        });
    }
    /**
     * Register a state change listener.
     *
     * @param listener - Function that will be called whenever state changes.
     * @returns Function to unregister this listener.
     */
    subscribe(listener) {
        const uniqueListener = wrap(listener);
        this._listeners.add(uniqueListener);
        return () => {
            this._listeners.delete(uniqueListener);
        };
    }
    /** @internal */
    _load(baseState, extraState) {
        assert_1.default(!this._loaded);
        this._baseState = baseState;
        this._derivedState = this._derive(baseState);
        this._extraState = extraState;
        this._loaded = true;
        this._scheduleUpdate();
        if (this.onLoaded) {
            this.onLoaded();
        }
    }
    /** @internal */
    _update(baseStateUpdate, extraStateUpdate) {
        assert_1.default(this._baseState && this._derivedState);
        const oldState = this.state;
        if (baseStateUpdate) {
            this._baseState = this._reduce(this._baseState, baseStateUpdate);
        }
        // Always running this lets us derive state based on passage of time, like baseRate decay
        this._derivedState = this._reduceDerived(this._derivedState, this._derive(this._baseState));
        if (extraStateUpdate) {
            assert_1.default(this._extraState);
            this._extraState = this._reduceExtra(this._extraState, extraStateUpdate);
        }
        this._scheduleUpdate();
        this._notify({
            newState: this.state,
            oldState,
            stateChange: difference(this.state, oldState)
        });
    }
}
exports.LiquityStore = LiquityStore;
//# sourceMappingURL=LiquityStore.js.map